import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import PageHeader from '../components/PageHeader';
import { getUserInfo, getUserQuestionnaireData, updateUserQuestionnaireData, updateUserEmail, updateUserPassword, updateUserInfo } from '../services/api';
import '../styles/userSettings.css';

const UserSettings = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [questionnaireData, setQuestionnaireData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [formData, setFormData] = useState({
    age: '',
    gender: ''
  });

  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailAddresses, setEmailAddresses] = useState([]);
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [updatingUsername, setUpdatingUsername] = useState(false);

  // Helper function to convert numeric gender values back to text
  const mapGenderFromValue = (value) => {
    console.log('mapGenderFromValue called with:', value, 'type:', typeof value);
    const genderMap = {
      '1': 'male',
      '2': 'female',
      '3': 'other'
    };
    const result = genderMap[value] || '';
    console.log('mapGenderFromValue result:', result);
    return result;
  };

  // Helper function to convert text gender values to numeric for storage
  const mapGenderToValue = (gender) => {
    const genderMap = {
      'male': '1',
      'female': '2',
      'other': '3'
    };
    return genderMap[gender.toLowerCase()] || '3';
  };

  // Function to check if there are unsaved changes
  const checkForUnsavedChanges = (newFormData = formData) => {
    const currentAge = questionnaireData?.age || '';
    const currentGender = mapGenderFromValue(questionnaireData?.gender || '');
    
    const hasChanges = newFormData.age !== currentAge || newFormData.gender !== currentGender;
    setHasUnsavedChanges(hasChanges);
    return hasChanges;
  };

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        
        // Fetch user info and questionnaire data in parallel
        const [userData, questionnaireData] = await Promise.all([
          getUserInfo(),
          getUserQuestionnaireData()
        ]);

        setUser(userData);
        setQuestionnaireData(questionnaireData);
        
        // Initialize email addresses with current user email
        setEmailAddresses([{
          email: userData.email,
          verified: true,
          primary: true
        }]);
        setPrimaryEmail(userData.email);
        
        // Process questionnaire data - handle both formats
        let age = '';
        let gender = '';
        
        if (questionnaireData.Data && Array.isArray(questionnaireData.Data)) {
          // Format from /api/user-questionnaire-data/ endpoint
          console.log('Processing questionnaire data from Data array:', questionnaireData.Data);
          
          for (const item of questionnaireData.Data) {
            if (item.question === 'age') {
              age = item.answer;
            } else if (item.question === 'gender') {
              gender = mapGenderFromValue(item.answer);
            }
          }
        } else {
          // Format from /api/user-questionnaire/ endpoint
          console.log('Processing questionnaire data from direct properties:', questionnaireData);
          age = questionnaireData.age || '';
          gender = mapGenderFromValue(questionnaireData.gender) || '';
        }
        
        console.log('Processed age:', age);
        console.log('Processed gender:', gender);
        
        setFormData({
          age: age,
          gender: gender
        });
        
        console.log('Form data set to:', { age, gender });
        
        setError(null);
      } catch (error) {
        console.error('Error fetching user data:', error);
        if (error.message.includes('401') || error.message.includes('403')) {
          // Redirect to login for authentication errors in production
          navigate('/auth');
        } else {
          setError('Failed to load user data. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // Check for unsaved changes when questionnaire data or form data changes
  useEffect(() => {
    if (questionnaireData) {
      checkForUnsavedChanges();
    }
  }, [questionnaireData, formData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateUsername = async () => {
    try {
      setUpdatingUsername(true);
      setUsernameError('');
      
      if (!newUsername.trim()) {
        setUsernameError('Username is required');
        return;
      }

      const result = await updateUserInfo({ display_name: newUsername });
      
      console.log('API Response:', result);
      
      if (result.Code === 111) {
        // Update local user state with new display name
        setUser(prev => ({
          ...prev,
          display_name: newUsername
        }));
        setShowUsernameModal(false);
        setNewUsername('');
        setUsernameError('');
      } else {
        // Handle validation errors
        if (result.Code === 24 && result.Data) {
          const displayNameError = result.Data.find(error => error[0] === 'display_name');
          if (displayNameError) {
            setUsernameError(displayNameError[1]);
          } else {
            setUsernameError('Failed to update display name. Please try again.');
          }
        } else {
          setUsernameError('Failed to update display name. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error updating display name:', error);
      setUsernameError('Failed to update display name. Please try again.');
    } finally {
      setUpdatingUsername(false);
    }
  };

  const handleAddEmail = async () => {
    try {
      setEmailError('');
      
      if (!newEmail.trim()) {
        setEmailError('Email is required');
        return;
      }
      
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newEmail)) {
        setEmailError('Please enter a valid email address');
        return;
      }
      
      // Check if email already exists
      if (emailAddresses.some(addr => addr.email === newEmail)) {
        setEmailError('This email address is already associated with your account');
        return;
      }
      
      // Add new email address (unverified)
      const newEmailAddress = {
        email: newEmail,
        verified: false,
        primary: false
      };
      
      setEmailAddresses(prev => [...prev, newEmailAddress]);
      setNewEmail('');
      setEmailError('');
    } catch (error) {
      console.error('Error adding email:', error);
      setEmailError('Failed to add email. Please try again.');
    }
  };

  const handleMakePrimary = (email) => {
    setEmailAddresses(prev => 
      prev.map(addr => ({
        ...addr,
        primary: addr.email === email
      }))
    );
    setPrimaryEmail(email);
  };

  const handleResendVerification = (email) => {
    console.log('Resending verification to:', email);
    // Here you would typically make an API call to resend verification
  };

  const handleRemoveEmail = (email) => {
    if (emailAddresses.length === 1) {
      setEmailError('You must have at least one email address');
      return;
    }
    
    setEmailAddresses(prev => prev.filter(addr => addr.email !== email));
    
    // If removing primary email, make the first remaining email primary
    if (email === primaryEmail) {
      const remainingEmails = emailAddresses.filter(addr => addr.email !== email);
      if (remainingEmails.length > 0) {
        setPrimaryEmail(remainingEmails[0].email);
        setEmailAddresses(prev => 
          prev.filter(addr => addr.email !== email).map((addr, index) => ({
            ...addr,
            primary: index === 0
          }))
        );
      }
    }
  };

  const handleUpdatePassword = async () => {
    try {
      setPasswordError('');
      
      // Basic validation
      if (!passwordData.old_password || !passwordData.new_password || !passwordData.confirm_password) {
        setPasswordError('All password fields are required');
        return;
      }
      
      if (passwordData.new_password !== passwordData.confirm_password) {
        setPasswordError('New passwords must match');
        return;
      }
      
      if (passwordData.new_password.length < 8) {
        setPasswordError('New password must be at least 8 characters long');
        return;
      }
      
      const result = await updateUserPassword(passwordData);
      
      if (result.Code === 111) {
        setShowPasswordModal(false);
        setPasswordData({
          old_password: '',
          new_password: '',
          confirm_password: ''
        });
        setPasswordError('');
      } else {
        setPasswordError(result.error || 'Failed to update password');
      }
    } catch (error) {
      console.error('Error updating password:', error);
      setPasswordError('Failed to update password. Please try again.');
    }
  };

  const handleCloseAccount = () => {
    console.log('Close account clicked');
    // Here you would typically show a confirmation modal
  };

  const handleAgeChange = (value) => {
    const newFormData = {
      ...formData,
      age: value
    };
    setFormData(newFormData);
    checkForUnsavedChanges(newFormData);
  };

  const handleGenderChange = (value) => {
    const newFormData = {
      ...formData,
      gender: value
    };
    setFormData(newFormData);
    checkForUnsavedChanges(newFormData);
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      setSaveMessage('');
      
      // Only send fields that have changed
      const changes = {};
      const currentAge = questionnaireData?.age || '';
      const currentGender = mapGenderFromValue(questionnaireData?.gender || '');
      
      console.log('Current data:', { currentAge, currentGender });
      console.log('Form data:', formData);
      
      if (formData.age !== currentAge) {
        changes.age = formData.age;
      }
      if (formData.gender !== currentGender) {
        changes.gender = mapGenderToValue(formData.gender);
      }
      
      console.log('Changes to save:', changes);
      
      if (Object.keys(changes).length === 0) {
        return;
      }
      
      let result;
      
      result = await updateUserQuestionnaireData(changes);
      
      if (result.Code === 111) {
        // Update local questionnaire data with the original backend format
        const updatedQuestionnaireData = { ...questionnaireData };
        if (changes.age !== undefined) {
          updatedQuestionnaireData.age = changes.age;
        }
        if (changes.gender !== undefined) {
          updatedQuestionnaireData.gender = changes.gender; // Store the numeric value
        }
        setQuestionnaireData(updatedQuestionnaireData);
        setHasUnsavedChanges(false); // Reset unsaved changes flag
      } else {
        console.error('Failed to save changes:', result);
      }
    } catch (error) {
      console.error('Error saving changes:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Sidebar user={user} />
        <div className="main-sec">
          <div className="user-settings-container">
            <PageHeader title="Account Settings" />
            <div className="settings-content">
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading user data...</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error || !user) {
    return (
      <>
        <Sidebar user={user} />
        <div className="main-sec">
          <div className="user-settings-container">
            <PageHeader title="Account Settings" />
            <div className="settings-content">
              <div className="error-message">
                <h2>Error Loading User Data</h2>
                <p>{error || 'Failed to load user information.'}</p>
                <button onClick={() => window.location.reload()}>Try Again</button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Sidebar user={user} />
      <div className="main-sec">
        <div className="user-settings-container">
          <PageHeader title="Account Settings" />
          
          <div className="settings-content">
            {/* Combined Settings Section */}
            <div className="settings-section">
              {/* Username Section */}
              <div className="profile-picture-section">
                <div className="profile-picture">
                  <div className="avatar-placeholder">
                    {(user.display_name || user.username).charAt(0).toUpperCase()}
                  </div>
                  <div className="username-display">
                    <span className="username-text">{user.display_name || user.username}</span>
                    <span className="account-active-since">Account active since {user.date_joined}</span>
                  </div>
                </div>
                
                <div className="picture-actions">
                  <button className="btn-email-update" onClick={() => {
                    setNewUsername(user.display_name || user.username);
                    setShowUsernameModal(true);
                  }}>
                    Update Username
                  </button>
                </div>
              </div>
              
              {/* Email Section */}
              <div className="settings-row">
                <div className="settings-left">
                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                  </div>
                  <div className="email-display">
                    <i className="fa-solid fa-envelope"></i>
                    <span>{user.email}</span>
                  </div>
                </div>
                <div className="settings-right">
                  <div className="form-group">
                    <label>&nbsp;</label>
                  </div>
                  <div className="button-group">
                    <button className="btn-email-update" onClick={() => setShowEmailModal(true)}>
                      Update Email
                    </button>
                    <button className="btn-secondary" onClick={() => setShowPasswordModal(true)}>
                      Change Password
                    </button>
                    <button className="btn-danger" onClick={handleCloseAccount}>
                      Close account
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Age and Gender Section */}
              <div className="settings-row">
                <div className="settings-left">
                  <div className="form-group">
                    <label htmlFor="age">Age</label>
                  </div>
                  <div className="age-display">
                    <select 
                      value={formData.age} 
                      onChange={(e) => handleAgeChange(e.target.value)} 
                      className="age-select"
                    >
                      <option value="">Select your age</option>
                      {Array.from({ length: 87 }, (_, i) => i + 13).map((age) => (
                        <option key={age} value={age.toString()}>
                          {age}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="settings-right">
                  <div className="form-group">
                    <label htmlFor="gender">Gender</label>
                  </div>
                  <div className="settings-gender-grid">
                    {["Male", "Female", "Other"].map((option) => (
                      <label
                        key={option}
                        className={`settings-gender-option ${formData.gender === option.toLowerCase() ? "selected" : ""}`}
                      >
                        <input
                          type="radio"
                          name="gender"
                          value={option.toLowerCase()}
                          checked={formData.gender === option.toLowerCase()}
                          onChange={(e) => handleGenderChange(e.target.value)}
                          className="settings-gender-input"
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Save Changes Section - Match Profile Section Layout */}
              <div className="profile-picture-section">
                <div className="info-text">
                  <p>Changes to age and gender will update your personalized recommendations.</p>
                </div>
                <div className="save-changes-section">
                  <button 
                    className={hasUnsavedChanges ? 'btn-email-update' : 'btn-secondary'}
                    onClick={handleSaveChanges}
                    disabled={saving || !hasUnsavedChanges}
                  >
                    {saving ? 'Saving...' : hasUnsavedChanges ? 'Save Changes' : 'No Changes'}
                  </button>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Username Change Modal */}
      {showUsernameModal && (
        <div className="user-settings-modal-overlay" onClick={() => {
          setShowUsernameModal(false);
          setUsernameError('');
          setNewUsername('');
        }}>
          <div className="user-settings-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="user-settings-modal-header">
              <h2>Change Username</h2>
              <button 
                className="user-settings-modal-close"
                onClick={() => {
                  setShowUsernameModal(false);
                  setUsernameError('');
                  setNewUsername('');
                }}
              >
                ×
              </button>
            </div>
            
            <div className="user-settings-modal-body">
              <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
                {/* Hidden password field to trick browsers */}
                <input 
                  type="password" 
                  style={{ display: 'none' }} 
                  autoComplete="new-password"
                />
                <div className="form-group">
                  <label htmlFor="newUsername">Username</label>
                  <input
                    type="text"
                    id="newUsername"
                    name="username_field"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    autoFocus
                    autoComplete="off"
                    autocorrect="off"
                    autocapitalize="off"
                    spellcheck="false"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-form-type="other"
                    data-autocomplete="off"
                    data-bws="off"
                    data-bws-ignore="true"
                  />
                  {usernameError && (
                    <div className="error-message-small">
                      {usernameError}
                    </div>
                  )}
                </div>
              </form>
              
              <div className="user-settings-modal-info">
                <i className="fa-solid fa-circle-exclamation"></i>
                <div className="info-text">
                  <p>Your username is used in Pickl Groups. It is not used for logging in to your Pickl account.</p>
                </div>
              </div>
              
              <div className="user-settings-modal-actions">
                <button 
                  className="btn-secondary" 
                  onClick={() => {
                    setShowUsernameModal(false);
                    setUsernameError('');
                    setNewUsername('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="btn-primary" 
                  onClick={handleUpdateUsername}
                  disabled={!newUsername.trim() || updatingUsername}
                >
                  {updatingUsername ? 'Updating...' : 'Update Username'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Management Modal */}
      {showEmailModal && (
        <div className="user-settings-modal-overlay" onClick={() => setShowEmailModal(false)}>
          <div className="user-settings-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="user-settings-modal-header">
              <h2>E-mail Addresses</h2>
              <button 
                className="user-settings-modal-close"
                onClick={() => setShowEmailModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="user-settings-modal-body">
              <p className="email-intro-text">The following e-mail addresses are associated with your account:</p>
              
              {/* Email Addresses List */}
              <div className="email-addresses-list">
                {emailAddresses.map((emailAddr, index) => (
                  <div key={index} className="email-address-item">
                    <div className="email-address-info">
                      <div className="email-status">
                        <span className="email-text">{emailAddr.email}</span>
                      </div>
                      <div className="email-status-text">
                        {emailAddr.primary ? 'Primary' : emailAddr.verified ? 'Verified' : 'Unverified'}
                        {emailAddr.primary && !emailAddr.verified ? ' Unverified' : ''}
                      </div>
                    </div>
                    <div className="email-address-actions">
                      {!emailAddr.primary && (
                        <button 
                          className="btn-email-update btn-small"
                          onClick={() => handleMakePrimary(emailAddr.email)}
                        >
                          Make Primary
                        </button>
                      )}
                      {!emailAddr.verified && (
                        <button 
                          className="btn-secondary btn-small"
                          onClick={() => handleResendVerification(emailAddr.email)}
                        >
                          Re-send Verification
                        </button>
                      )}
                      {!emailAddr.primary && (
                        <button 
                          className="btn-danger btn-small"
                          onClick={() => handleRemoveEmail(emailAddr.email)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Add Email Section */}
              <div className="add-email-section">
                <h3>Add E-mail Address</h3>
                <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
                  <div className="form-group">
                    <label htmlFor="newEmail">E-mail</label>
                    <input
                      type="email"
                      id="newEmail"
                      name="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="E-mail address"
                      autoComplete="off"
                      autocorrect="off"
                      autocapitalize="off"
                      spellcheck="false"
                    />
                    {emailError && (
                      <div className="error-message-small">
                        {emailError}
                      </div>
                    )}
                  </div>
                </form>
                <button 
                  className="btn-primary"
                  onClick={handleAddEmail}
                  disabled={!newEmail.trim()}
                >
                  Add E-mail
                </button>
              </div>
              
              <div className="user-settings-modal-actions">
                <button 
                  className="btn-secondary" 
                  onClick={() => setShowEmailModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="user-settings-modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="user-settings-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="user-settings-modal-header">
              <h2>Change Password</h2>
              <button 
                className="user-settings-modal-close"
                onClick={() => setShowPasswordModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="user-settings-modal-body">
              <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
                <div className="form-group">
                  <label htmlFor="oldPassword">Current Password</label>
                  <input
                    type="password"
                    id="oldPassword"
                    name="old_password"
                    value={passwordData.old_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, old_password: e.target.value }))}
                    placeholder="Enter your current password"
                    autoFocus
                    autoComplete="current-password"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    type="password"
                    id="newPassword"
                    name="new_password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                    placeholder="Enter your new password"
                    autoComplete="new-password"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirm_password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                    placeholder="Confirm your new password"
                    autoComplete="new-password"
                  />
                </div>
                
                {passwordError && (
                  <div className="error-message-small">
                    {passwordError}
                  </div>
                )}
              </form>
              
              <div className="user-settings-modal-info">
                <i className="fa-solid fa-circle-exclamation"></i>
                <div className="info-text">
                  <p>Your password must be at least 8 characters long and should include a mix of letters, numbers, and special characters for security.</p>
                </div>
              </div>
              
              <div className="user-settings-modal-actions">
                <button 
                  className="btn-secondary" 
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordData({
                      old_password: '',
                      new_password: '',
                      confirm_password: ''
                    });
                    setPasswordError('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="btn-primary" 
                  onClick={handleUpdatePassword}
                  disabled={!passwordData.old_password || !passwordData.new_password || !passwordData.confirm_password}
                >
                  Update Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserSettings; 